import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { lazyAuth } from "@/lib/supabase-lazy";
import { authErrorText } from "@/lib/auth-errors";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/auth/AuthModal";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Välj nytt lösenord — Sellavo" },
      { name: "description", content: "Välj ett nytt lösenord till ditt Sellavo-konto." },
      { property: "og:title", content: "Välj nytt lösenord — Sellavo" },
      { property: "og:description", content: "Välj ett nytt lösenord till ditt Sellavo-konto." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const { data: sub } = lazyAuth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) setReady(true);
      if (session?.user?.email) setEmail(session.user.email);
    });
    lazyAuth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
      if (data.session?.user?.email) setEmail(data.session.user.email);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError("Lösenordet behöver vara minst 8 tecken.");
    if (!/[A-Za-zÅÄÖåäö]/.test(password) || !/[0-9]/.test(password))
      return setError("Lösenordet behöver innehålla både bokstäver och siffror.");
    if (password !== confirm) return setError("Lösenorden matchar inte.");
    setLoading(true);
    try {
      const { error } = await lazyAuth.updateUser({ password });
      if (error) throw error;
      setDone(true);
      setTimeout(() => navigate({ to: "/" }), 2000);
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-[80vh] items-center justify-center overflow-hidden px-4 py-16">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-ocean-300 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-ocean-500 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-xl">
        {done ? (
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ocean-100 text-ocean-700">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground">
              Lösenordet är uppdaterat
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">Du skickas vidare till startsidan…</p>
          </div>
        ) : (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ocean-100 text-ocean-700">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground">
              Välj ett nytt lösenord
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Välj något du inte använder någon annanstans — minst 6 tecken.
            </p>

            {!ready && (
              <p className="mt-4 rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
                Öppna den här sidan via länken i återställningsmejlet.{" "}
                <Link to="/glomt-losenord" className="text-primary underline underline-offset-2">
                  Skicka en ny länk
                </Link>
              </p>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                type="hidden"
                name="username"
                autoComplete="username"
                value={email ?? ""}
                readOnly
              />
              <div className="space-y-2">
                <Label htmlFor="new-password">Nytt lösenord</Label>
                <PasswordInput
                  id="new-password"
                  name="new-password"
                  value={password}
                  onChange={setPassword}
                  placeholder="Ange nytt lösenord"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Bekräfta lösenord</Label>
                <PasswordInput
                  id="confirm-password"
                  name="confirm-password"
                  value={confirm}
                  onChange={setConfirm}
                  placeholder="Ange lösenordet igen"
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={loading || !ready}>
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Spara nytt lösenord
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
